import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, primeCsrfToken } from "@/api/client";

/** Prime CSRF cookie + axios cache before unauthenticated POSTs. */
async function primeCsrf() {
  await primeCsrfToken();
}

export default function Signup() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    email: "",
    password: "",
    companyName: "",
  });
  const [loading, setLoading] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);

  const clearFieldErrors = () =>
    setFieldErrors({ email: "", password: "", companyName: "" });

  const handleResendVerification = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    try {
      await primeCsrfToken();
      await api.auth.resendVerification({ email: trimmed });
      sessionStorage.setItem("pending_verify_email", trimmed);
      setError("");
      navigate("/verify-email");
    } catch {
      setError("Could not send email. Please try again.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setEmailNotVerified(false);
    clearFieldErrors();
    setLoading(true);

    const trimmedEmail = email.trim();
    const trimmedCompany = companyName.trim();

    if (!trimmedEmail || !password || !trimmedCompany) {
      setFieldErrors({
        email: !trimmedEmail ? "Required" : "",
        password: !password ? "Required" : "",
        companyName: !trimmedCompany ? "Required" : "",
      });
      setError("Please complete all required fields.");
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setFieldErrors((f) => ({ ...f, password: "Use at least 8 characters." }));
      setError("Password must be at least 8 characters.");
      setLoading(false);
      return;
    }

    try {
      await primeCsrf();
      await api.auth.signup({
        email: trimmedEmail,
        password,
        full_name: trimmedCompany,
        company_name: trimmedCompany,
        plan: "trial",
      });
      sessionStorage.setItem("pending_verify_email", trimmedEmail);
      navigate("/verify-email");
    } catch (err) {
      const code = err.response?.data?.error;
      if (code === "EMAIL_NOT_VERIFIED") {
        setEmailNotVerified(true);
        setError("");
        return;
      }

      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "";
      const lower = String(msg).toLowerCase();

      if (
        lower.includes("already") ||
        lower.includes("registered") ||
        lower.includes("exists") ||
        lower.includes("duplicate")
      ) {
        setError(
          "An account with this email exists. Sign in instead."
        );
      } else if (
        lower.includes("missing required fields") ||
        lower.includes("missing required")
      ) {
        setError("Please complete all required fields.");
        setFieldErrors({
          email: !trimmedEmail ? "Required" : "",
          password: !password ? "Required" : "",
          companyName: !trimmedCompany ? "Required" : "",
        });
      } else {
        setError(
          typeof msg === "string" && msg
            ? msg
            : "Something went wrong. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ad-bg">
      <div className="w-full max-w-md bg-white border border-ad-border rounded-[10px] p-8 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
        <div className="text-center mb-8">
          <img
            src="/logo.png"
            alt="Agent David"
            className="w-12 h-12 object-contain mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-gray-900">Agent David</h1>
          <p className="text-gray-600 mt-1">Start your 7-day free trial</p>
          <p className="text-sm text-gray-400 mt-2">No credit card required</p>
        </div>

        {emailNotVerified && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
            <p>Account exists but email not verified.</p>
            <button
              type="button"
              onClick={handleResendVerification}
              className="underline font-medium mt-2 inline-block"
            >
              Resend verification email
            </button>
            {error ? (
              <p className="mt-2 text-sm text-red-800">{error}</p>
            ) : null}
          </div>
        )}
        {error && !emailNotVerified && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="signup-email"
              className="block text-sm font-medium text-gray-600 mb-1"
            >
              Work email *
            </label>
            <input
              id="signup-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 bg-white border border-ad-border text-gray-900 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-ad-primary/30"
              placeholder="you@company.com"
            />
            {fieldErrors.email && (
              <p className="mt-1 text-xs text-red-600">{fieldErrors.email}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="signup-password"
              className="block text-sm font-medium text-gray-600 mb-1"
            >
              Password * <span className="font-normal text-gray-500">(min 8 characters)</span>
            </label>
            <input
              id="signup-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-3 py-2 bg-white border border-ad-border text-gray-900 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-ad-primary/30"
              placeholder="Create a password"
            />
            {fieldErrors.password && (
              <p className="mt-1 text-xs text-red-600">{fieldErrors.password}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="company-name"
              className="block text-sm font-medium text-gray-600 mb-1"
            >
              Company name *
            </label>
            <input
              id="company-name"
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
              className="w-full px-3 py-2 bg-white border border-ad-border text-gray-900 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-ad-primary/30"
              placeholder="Your company"
            />
            {fieldErrors.companyName && (
              <p className="mt-1 text-xs text-red-600">{fieldErrors.companyName}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 px-4 bg-ad-primary text-white rounded-[8px] hover:bg-ad-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ad-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Starting…" : "Start free trial →"}
          </button>
        </form>

        <p className="text-center text-sm text-gray-400 mt-4">
          Already have an account?{" "}
          <Link
            to="/login"
            className="text-ad-primary font-medium hover:underline"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
